import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";

interface SaveProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
  currentProposalName: string;
}

export const SaveProposalDialog = ({ open, onOpenChange, onSave, currentProposalName }: SaveProposalDialogProps) => {
  const [proposalName, setProposalName] = useState(currentProposalName);
  const [error, setError] = useState("");

  const handleSave = () => {
    const trimmedName = proposalName.trim();
    
    if (!trimmedName) {
      setError("Please enter a proposal name");
      return;
    }

    if (trimmedName.length > 100) {
      setError("Proposal name must be less than 100 characters");
      return;
    }

    onSave(trimmedName);
    onOpenChange(false);
    setError("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setError("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Save Proposal</DialogTitle>
          <DialogDescription>
            Enter a name for your proposal. It will be saved to your browser's local storage.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="proposal-name">Proposal Name</Label>
            <Input
              id="proposal-name"
              value={proposalName}
              onChange={(e) => {
                setProposalName(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
              }}
              placeholder="e.g., Smith Kitchen Remodel"
              autoFocus
            />
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {currentProposalName && proposalName === currentProposalName && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                This will update the existing proposal "{currentProposalName}"
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
            Save Proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
